import React, { useState, useRef, useEffect, useCallback } from 'react';

export interface MealInputProps {
  diaryDayId: number;
  createMealUrl: string;
  suggestionsUrl: string;
  placeholder: string;
  csrfToken: string;
  testId?: string;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function MealInput({
  diaryDayId,
  createMealUrl,
  suggestionsUrl,
  placeholder,
  csrfToken,
  testId,
}: MealInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    const url = `${suggestionsUrl}?query=${encodeURIComponent(query)}&diary_day_id=${diaryDayId}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [suggestionsUrl, diaryDayId]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const query = value.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce the API call
    timeoutRef.current = window.setTimeout(() => {
      fetchSuggestions(query);
    }, 300);
  };

  const submitMeal = async (mealName: string) => {
    if (!mealName.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setSuggestions([]);
    setShowSuggestions(false);

    try {
      const formData = new FormData();
      formData.append('foods_meal[name]', mealName);

      const response = await fetch(createMealUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        },
        body: formData,
      });

      if (response.ok) {
        setInputValue('');
        // Turbo will handle the response and update the meal list
        const html = await response.text();
        // Process Turbo Stream response
        if (html.includes('turbo-stream')) {
          const template = document.createElement('template');
          template.innerHTML = html;
          document.body.appendChild(template.content);
        }
      }
    } catch (error) {
      console.error('Error submitting meal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setInputValue(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    submitMeal(suggestion);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    submitMeal(inputValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="position-relative mt-3" style={{ minWidth: '200px', flex: 1 }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          id="meal-input"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          disabled={isSubmitting}
          data-test-id={testId}
        />

        <button
          className="btn btn-primary btn-sm position-absolute end-0 top-0 bottom-0 me-1 my-1"
          type="submit"
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          disabled={isSubmitting || !inputValue.trim()}
          data-test-id="meal-submit-button"
        >
          <i className="bi bi-plus-lg" />
        </button>

        {/* Autocomplete suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="list-group position-absolute w-100 mt-1"
            style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="list-group-item list-group-item-action"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </form>
  );
}

export default MealInput;
